import { Button } from "@shared/ui/button";
import { useTranslation } from "react-i18next";

interface ServiceDetailsReviewProps {
  data?: any;
  onEdit?: () => void;
}

export function ServiceDetailsReview({
  data,
  onEdit,
}: ServiceDetailsReviewProps) {
  const { t } = useTranslation();
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">{t("Service Details")}</h2>
        <Button variant="outline" size="sm" onClick={onEdit}>
          Edit
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Service Start Date */}
        <div>
          <p className="text-sm text-gray-600 mb-1">{t("Service Start Date")}</p>
          <p className="text-base text-gray-900 font-medium">
            {data?.service_start_date || t("Not specified")}
          </p>
        </div>

        {/* Preferred Contact Method */}
        <div>
          <p className="text-sm text-gray-600 mb-1">{t("Preferred Contact Method")}</p>
          <p className="text-base text-gray-900 font-medium">
            {data?.preferred_contact_methods && data.preferred_contact_methods.length > 0
              ? data.preferred_contact_methods.join(", ")
              : t("Not specified")}
          </p>
        </div>

        {/* Selected Services */}
        <div className="md:col-span-1">
          <p className="text-sm text-gray-600 mb-1">{t("Selected Services")}</p>
          <p className="text-base text-gray-900 font-medium">
            {data?.selected_services && data.selected_services.length > 0
              ? data.selected_services
                  .map((service: string) => {
                    switch (service) {
                      case "water":
                        return t("Water Supply");
                      case "sewerage":
                        return t("Sewerage Service");
                      case "waste":
                        return t("Waste Collection");
                      default:
                        return service;
                    }
                  })
                  .join(", ")
              : t("None selected")}
          </p>
        </div>
         <div>
          <p className="text-sm text-gray-600 mb-1">{t("Subscribed for Paperless Billing")}</p>
          <p className="text-base text-gray-900 font-medium">
            {data?.paperless_billing
              ? t("Yes")
              : t("No")}
          </p>
        </div>

        {/* Property Type / What best describes this connection? */}
        <div className="col-span-1 md:col-span-2">
          <p className="text-sm text-gray-600 mb-1">{t("What best describes this connection?")}</p>
          <p className="text-base text-gray-900 font-medium">
            {(() => {
              switch (data?.property_type) {
                case "individual":
                  return t("A standalone property (Individual Account)");
                case "tenant":
                  return t("A rented property - I am a tenant (Sub Account)");
                case "business":
                  return t("Multi-location business or group");
                default:
                  return data?.property_type || t("Not specified");
              }
            })()}
          </p>
        </div>

        {data?.property_type === "tenant" && (
          <div className="col-span-1 md:col-span-2">
            <hr className="mb-6 border-gray-200" />
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t("Owner Details")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-600 mb-1">{t("Owner Name")}</p>
                <p className="text-base text-gray-900 font-medium">
                  {data?.ownerDetails?.name || t("Not specified")}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">{t("Owner Phone")}</p>
                <p className="text-base text-gray-900 font-medium">
                  {data?.ownerDetails?.phone || t("Not specified")}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">{t("Owner Email")}</p>
                <p className="text-base text-gray-900 font-medium">
                  {data?.ownerDetails?.email || t("Not specified")}
                </p>
              </div>
              <div className="col-span-1 md:col-span-2">
                <p className="text-sm text-gray-600 mb-1">{t("Owner Address")}</p>
                <p className="text-base text-gray-900 font-medium">
                  {data?.ownerDetails?.address || t("Not specified")}
                </p>
              </div>
            </div>
          </div>
        )}

        {data?.property_type === "business" && (
          <div className="col-span-1 md:col-span-2">
            <hr className="mb-6 border-gray-200" />
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t("Business Account Linking")}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="col-span-1 md:col-span-2">
                <p className="text-sm text-gray-600 mb-1">
                  {t("Link to Existing Business Account")}
                </p>
                <p className="text-base text-gray-900 font-medium">
                  {data?.business_account_link === "existing"
                    ? t("Yes, link to an existing business account")
                    : t("No, create this as a new main business account")}
                </p>
              </div>
            </div>

            {data?.business_account_link === "existing" && (
              <>
                <hr className="my-6 border-gray-200" />
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {t("Existing Business Account Details")}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">
                      {t("Business Account Number")}
                    </p>
                    <p className="text-base text-gray-900 font-medium">
                      {data?.existing_business_account?.account_number ||
                        "Not specified"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">
                      {t("Business Account Email")}
                    </p>
                    <p className="text-base text-gray-900 font-medium">
                      {data?.existing_business_account?.account_email ||
                        "Not specified"}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}